/*
 * FEDERAÇÃO DOS PROFISSIONAIS DA BELEZA
 * MODAL CADASTRO FILIADO - DASHBOARD ADMIN
 * Arquivo: src/components/Dashboard_ADM/ModalCadastroFiliado.tsx
 * UPDATED: 2026-04-30 — v3.7
 * ✅ BUGFIX: gerarIdAutomatico agora verifica o 2º ID gerado antes de confirmar
 * ✅ BUGFIX: catch de gerarIdAutomatico não confirma ID sem verificação
 * ✅ Busca de escola para vincular ao filiado cadastrado
 * ✅ Barra de busca rápida admin para vincular cadastro à escola
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  X, ChevronRight, ChevronLeft, Search, Upload, RefreshCw,
  CheckCircle, Loader2, AlertCircle, ImageIcon, Trash2,
  FileText, User, Building2, Link2, XCircle,
} from 'lucide-react';
import apiClient from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────
interface DadosEtapa1 {
  nomeSolicitante: string; cpf: string; cnpj: string;
  nomeFantasia: string; dataNascimento: string; cep: string;
  endereco: string; cidade: string; estado: string;
  complemento: string; telefone: string; email: string;
  isSchool: boolean; website: string; identidadeInternacional: string;
}
interface DadosEtapa2 {
  funcao: string; dataInicioAtividade: string;
  experienciaAnos: number; especializacoes: string;
}
interface UploadSlot {
  arquivo: File | null; preview: string | null; url: string | null;
  enviado: boolean; enviando: boolean; erro: string | null;
}
interface EscolaResult {
  id: string;
  systemId: string;
  schoolName: string;
  city?: string;
  state?: string;
  email?: string;
}
interface Props { onFechar: () => void; onSucesso: () => void; }

const slotVazio = (): UploadSlot => ({
  arquivo: null, preview: null, url: null,
  enviado: false, enviando: false, erro: null,
});

// ─── Card de upload ───────────────────────────────────────────────────────────
const UploadCard: React.FC<{
  icon: React.ReactNode; titulo: string; descricao: string; badge?: string;
  slot: UploadSlot; accept?: string;
  onSelecionar: (f: File) => void; onRemover: () => void;
}> = ({ icon, titulo, descricao, badge, slot, accept = 'image/*', onSelecionar, onRemover }) => (
  <div className={`rounded-xl border-2 overflow-hidden transition-all
    ${slot.enviado
      ? 'border-green-400 bg-green-50'
      : slot.arquivo
      ? 'border-blue-300 bg-blue-50/30'
      : 'border-dashed border-gray-300 bg-white hover:border-blue-400 hover:bg-gray-50'}`}>
    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 bg-white/70">
      <span className="text-gray-500">{icon}</span>
      <div className="flex-1">
        <p className="text-xs font-bold text-gray-700 uppercase tracking-wide leading-none">{titulo}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">{descricao}</p>
      </div>
      {badge && (
        <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5
          rounded-full bg-amber-100 text-amber-700 border border-amber-200 flex-shrink-0">
          {badge}
        </span>
      )}
    </div>
    {slot.enviado ? (
      <div className="flex items-center gap-3 px-4 py-3">
        {slot.preview
          ? <img src={slot.preview} alt="" className="w-12 h-12 rounded-lg object-cover border border-green-300" />
          : <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-green-600" />
            </div>}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-green-700 truncate">{slot.arquivo?.name}</p>
          <p className="text-[10px] text-green-500 mt-0.5 flex items-center gap-1">
            <CheckCircle className="w-3 h-3" /> Enviado com sucesso
          </p>
        </div>
      </div>
    ) : slot.arquivo ? (
      <div className="px-4 py-3">
        {slot.preview && (
          <img src={slot.preview} alt="Preview"
            className="w-full max-h-32 object-contain rounded-lg mb-2 border border-gray-200" />
        )}
        <div className="flex items-center gap-2">
          <ImageIcon className="w-4 h-4 text-blue-500 flex-shrink-0" />
          <span className="text-xs text-gray-700 truncate flex-1">{slot.arquivo.name}</span>
          <span className="text-[10px] text-gray-400 flex-shrink-0">
            {(slot.arquivo.size / 1024).toFixed(0)} KB
          </span>
          <button onClick={onRemover}
            className="p-1 rounded-lg bg-red-100 text-red-500 hover:bg-red-200 transition-colors flex-shrink-0">
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
        {slot.erro && (
          <p className="text-xs text-red-500 mt-1.5 flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> {slot.erro}
          </p>
        )}
      </div>
    ) : (
      <label className="flex flex-col items-center justify-center py-5 cursor-pointer">
        <div className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center mb-2">
          <Upload className="w-4 h-4 text-gray-400" />
        </div>
        <p className="text-xs text-gray-500 font-medium">Clique para selecionar</p>
        <p className="text-[10px] text-gray-400 mt-0.5">
          {accept.includes('pdf') ? 'JPG, PNG, PDF' : 'JPG, PNG, WebP'} — máx. 5 MB
        </p>
        <input type="file" accept={accept} className="hidden"
          onChange={e => { const f = e.target.files?.[0]; if (f) onSelecionar(f); }} />
      </label>
    )}
  </div>
);

// ─── Busca de Escola ──────────────────────────────────────────────────────────

const BuscaEscola: React.FC<{
  escolaSelecionada: EscolaResult | null;
  onSelecionar: (escola: EscolaResult | null) => void;
}> = ({ escolaSelecionada, onSelecionar }) => {
  const [query, setQuery]           = useState('');
  const [resultados, setResultados] = useState<EscolaResult[]>([]);
  const [buscando, setBuscando]     = useState(false);
  const [aberto, setAberto]         = useState(false);
  const [dropPos, setDropPos]       = useState({ top: 0, left: 0, width: 0 });

  // ── Modal de Recentes ──────────────────────────────────────────────────────
  const [modalRecentes, setModalRecentes]   = useState(false);
  const [recentes, setRecentes]             = useState<EscolaResult[]>([]);
  const [carregandoRec, setCarregandoRec]   = useState(false);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef    = useRef<HTMLInputElement>(null);

  const atualizarPos = () => {
    if (inputRef.current) {
      const r = inputRef.current.getBoundingClientRect();
      setDropPos({ top: r.bottom + 4, left: r.left, width: r.width });
    }
  };

  // ── Busca filtrada pelo termo ──────────────────────────────────────────────
  const buscar = useCallback(async (termo: string) => {
    if (!termo.trim() || termo.length < 2) { setResultados([]); setAberto(false); return; }
    setBuscando(true);
    try {
      const data: any = await apiClient.get(
        `/affiliates?isSchool=true&search=${encodeURIComponent(termo.trim())}&limit=20&sort=createdAt&order=desc`
      );
      const lista: EscolaResult[] = (data.affiliates ?? data.data ?? data ?? []).map((a: any) => ({
        id:         a.id,
        systemId:   a.systemId ?? a.system_id ?? '',
        schoolName: a.schoolName?.trim() || a.nomeFantasia?.trim() || a.nomeSolicitante?.trim() || 'Sem nome',
        city:       a.city ?? a.cidade ?? '',
        state:      a.state ?? a.estado ?? '',
        email:      a.email ?? '',
      }));

      // ✅ Removido o filtro client-side: a API já filtra pelo termo.
      // Filtrar de novo só pelo schoolName descartava resultados válidos
      // (ex: quando o match veio de outro campo no backend).
      setResultados(lista);
      if (lista.length > 0) { atualizarPos(); setAberto(true); }
      else setAberto(false);
    } catch {
      setResultados([]); setAberto(false);
    } finally {
      setBuscando(false);
    }
  }, []);

  // ── Carrega recentes (últimas 10 escolas cadastradas) ──────────────────────
  const carregarRecentes = async () => {
    setCarregandoRec(true);
    try {
      const data: any = await apiClient.get(
        `/affiliates?isSchool=true&limit=10&sort=createdAt&order=desc`
      );
      const lista: EscolaResult[] = (data.affiliates ?? data.data ?? data ?? []).map((a: any) => ({
        id:         a.id,
        systemId:   a.systemId ?? a.system_id ?? '',
        schoolName: a.schoolName ?? a.nomeFantasia ?? a.nomeSolicitante ?? 'Sem nome',
        city:       a.city ?? a.cidade ?? '',
        state:      a.state ?? a.estado ?? '',
        email:      a.email ?? '',
      }));
      setRecentes(lista);
    } catch {
      setRecentes([]);
    } finally {
      setCarregandoRec(false);
    }
  };

  const abrirRecentes = () => {
    setModalRecentes(true);
    carregarRecentes();
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setQuery(v);
    if (v.length < 2) { setAberto(false); setResultados([]); }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => buscar(v), 400);
  };

  const selecionar = (escola: EscolaResult) => {
    onSelecionar(escola);
    setQuery(''); setResultados([]); setAberto(false);
    setModalRecentes(false);
  };

  const limpar = () => {
    onSelecionar(null);
    setQuery(''); setResultados([]); setAberto(false);
  };

  const handleBlur = () => { setTimeout(() => setAberto(false), 300); };

  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide">
          Vincular à Escola (opcional)
        </label>
        {/* ✅ Botão de cadastros recentes */}
        <button
          type="button"
          onClick={abrirRecentes}
          className="flex items-center gap-1.5 text-[11px] font-bold text-blue-600 hover:text-blue-800
            px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 border border-blue-200 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Cadastros recentes
        </button>
      </div>

      {escolaSelecionada ? (
        <div className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 border-blue-300 bg-blue-50">
          <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
            <Building2 className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-blue-900 truncate">{escolaSelecionada.schoolName}</p>
            <p className="text-[10px] text-blue-500 font-mono">
              {escolaSelecionada.systemId}
              {escolaSelecionada.city && ` · ${escolaSelecionada.city}/${escolaSelecionada.state}`}
            </p>
          </div>
          <button onClick={limpar}
            className="p-1.5 rounded-lg bg-blue-100 hover:bg-red-100 text-blue-400 hover:text-red-500 transition-colors flex-shrink-0"
            title="Desvincular escola">
            <XCircle className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="relative">
          <Search className="absolute left-3.5 top-3.5 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleChange}
            onFocus={() => { atualizarPos(); resultados.length > 0 && setAberto(true); }}
            onBlur={handleBlur}
            placeholder="Buscar escola por nome..."
            className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-300 focus:outline-none
              focus:ring-2 focus:ring-blue-500 text-gray-900 text-sm bg-white"
          />
          {buscando && (
            <Loader2 className="absolute right-3.5 top-3.5 w-4 h-4 text-blue-400 animate-spin" />
          )}

          {/* ✅ Dropdown limpo — mostra SOMENTE o nome da escola correspondente */}
          {aberto && (
            <div
              style={{
                position: 'fixed',
                top:   dropPos.top,
                left:  dropPos.left,
                width: dropPos.width,
                zIndex: 9999,
              }}
              className="bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden"
            >
              {resultados.length > 0 ? resultados.map(escola => (
                <button
                  key={escola.id}
                  onMouseDown={() => selecionar(escola)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-blue-50
                    transition-colors text-left border-b border-gray-100 last:border-0"
                >
                  <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  {/* ✅ Destaca a parte do nome que corresponde ao termo buscado */}
                  <span className="text-sm font-semibold text-gray-800 flex-1 truncate">
                    {highlightMatch(escola.schoolName, query)}
                  </span>
                  <Link2 className="w-3 h-3 text-blue-400 flex-shrink-0" />
                </button>
              )) : (
                <div className="px-4 py-3">
                  <p className="text-xs text-gray-400 text-center">Nenhuma escola encontrada</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-gray-400 mt-1">
        Vincule este filiado a uma escola já cadastrada no sistema
      </p>

      {/* ══ Modal de Cadastros Recentes ═══════════════════════════════════════ */}
      {modalRecentes && (
        <div
          className="fixed inset-0 bg-black/50 z-[10000] flex items-center justify-center p-4"
          onMouseDown={e => { if (e.target === e.currentTarget) setModalRecentes(false); }}
        >
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden flex flex-col max-h-[70vh]">

            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Building2 className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-black text-gray-900">Cadastros Recentes</p>
                  <p className="text-[10px] text-gray-400">Últimas escolas cadastradas no sistema</p>
                </div>
              </div>
              <button
                onClick={() => setModalRecentes(false)}
                className="p-1.5 hover:bg-gray-200 rounded-lg text-gray-400 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Lista */}
            <div className="flex-1 overflow-y-auto">
              {carregandoRec ? (
                <div className="flex items-center justify-center py-10">
                  <Loader2 className="w-6 h-6 text-blue-400 animate-spin" />
                </div>
              ) : recentes.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 gap-2">
                  <Building2 className="w-8 h-8 text-gray-300" />
                  <p className="text-sm text-gray-400">Nenhuma escola encontrada</p>
                </div>
              ) : (
                recentes.map((escola, i) => (
                  <button
                    key={escola.id}
                    onClick={() => selecionar(escola)}
                    className="w-full flex items-center gap-3 px-5 py-3.5 hover:bg-blue-50
                      transition-colors text-left border-b border-gray-100 last:border-0"
                  >
                    {/* Número de ordem */}
                    <span className="w-5 h-5 rounded-full bg-gray-100 text-[10px] font-black
                      text-gray-400 flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-800 truncate">{escola.schoolName}</p>
                      <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                        {escola.systemId}
                        {escola.city && (
                          <span className="text-gray-300"> · {escola.city}/{escola.state}</span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      <span className="text-[10px] text-blue-500 font-semibold">Vincular</span>
                      <Link2 className="w-3.5 h-3.5 text-blue-400" />
                    </div>
                  </button>
                ))
              )}
            </div>

          </div>
        </div>
      )}
    </div>
  );
};

// ─── Helper: destaca o trecho que bate com o termo de busca ───────────────────
const highlightMatch = (nome: string, termo: string): React.ReactNode => {
  if (!termo.trim()) return nome;
  const idx = nome.toLowerCase().indexOf(termo.toLowerCase());
  if (idx === -1) return nome;
  return (
    <>
      {nome.slice(0, idx)}
      <span className="bg-yellow-100 text-yellow-800 rounded px-0.5 font-black">
        {nome.slice(idx, idx + termo.length)}
      </span>
      {nome.slice(idx + termo.length)}
    </>
  );
};
// ─── Componente principal ─────────────────────────────────────────────────────
const ModalCadastroFiliado: React.FC<Props> = ({ onFechar, onSucesso }) => {
  const [etapa, setEtapa]             = useState<1 | 2 | 3>(1);
  const [salvando, setSalvando]       = useState(false);
  const [erro, setErro]               = useState<string | null>(null);
  const [sucesso, setSucesso]         = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  const [affiliateId, setAffiliateId]         = useState<string | null>(null);
  const [affiliateUserId, setAffiliateUserId] = useState<string | null>(null);
  const [systemId, setSystemId]               = useState<string | null>(null);
  const [registroFepcssp, setRegistroFepcssp] = useState<string | null>(null);
  const [escolaVinculada, setEscolaVinculada] = useState<EscolaResult | null>(null);

  // ─── ID do Filiado ────────────────────────────────────────────────────────
  const [gerandoId, setGerandoId]       = useState(false);
  const [idDisponivel, setIdDisponivel] = useState<boolean | null>(null);
  const [idCustom, setIdCustom]         = useState('');

  // ─── Nº FEPCSSP ───────────────────────────────────────────────────────────
  const [fepcsspCustom,     setFepcsspCustom]     = useState('');
  const [fepcsspDisponivel, setFepcsspDisponivel] = useState<boolean | null>(null);
  const [gerandoFepcssp,    setGerandoFepcssp]    = useState(false);

  const [cepLoading, setCepLoading] = useState(false);
  const [erros, setErros]           = useState<Record<string, string>>({});

  const [slotLogo,      setSlotLogo]      = useState<UploadSlot>(slotVazio());
  const [slotFoto,      setSlotFoto]      = useState<UploadSlot>(slotVazio());
  const [slotDocumento, setSlotDocumento] = useState<UploadSlot>(slotVazio());

  const [etapa1, setEtapa1] = useState<DadosEtapa1>({
    nomeSolicitante: '', cpf: '', cnpj: '', nomeFantasia: '',
    dataNascimento: '', cep: '', endereco: '', cidade: '',
    estado: '', complemento: '', telefone: '', email: '',
    isSchool: false, website: '', identidadeInternacional: '',
  });
  const [etapa2, setEtapa2] = useState<DadosEtapa2>({
    funcao: '', dataInicioAtividade: '', experienciaAnos: 0, especializacoes: '',
  });

  const inputBase = 'w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 text-sm font-medium bg-white';

  // ─── Helpers de slot ───────────────────────────────────────────────────────
  const selecionarArquivo = (set: React.Dispatch<React.SetStateAction<UploadSlot>>, file: File) => {
    const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
    set({ arquivo: file, preview, url: null, enviado: false, enviando: false, erro: null });
  };
  const removerArquivo = (set: React.Dispatch<React.SetStateAction<UploadSlot>>, slot: UploadSlot) => {
    if (slot.preview) URL.revokeObjectURL(slot.preview);
    set(slotVazio());
  };

  // ─── CEP ──────────────────────────────────────────────────────────────────
  const buscarCEP = async (cep: string) => {
    const num = cep.replace(/\D/g, '');
    if (num.length !== 8) return;
    setCepLoading(true);
    try {
      const res  = await fetch(`https://viacep.com.br/ws/${num}/json/`);
      const data = await res.json();
      if (!data.erro) setEtapa1(p => ({
        ...p, endereco: data.logradouro || p.endereco,
        cidade: data.localidade || p.cidade, estado: data.uf || p.estado,
      }));
    } finally { setCepLoading(false); }
  };

  // ─── ID do Filiado — CORRIGIDO ─────────────────────────────────────────────
  // BUG ANTERIOR: quando o 1º ID estava em uso, gerava um 2º sem verificar.
  // No catch, gerava um ID local sem verificação alguma.
  // CORREÇÃO: verifica o 2º ID também. No catch, não confirma o ID como disponível.
  const gerarIdAutomatico = async () => {
    setGerandoId(true); setIdDisponivel(null);
    const ano = new Date().getFullYear();

    const gerarCandidato = () =>
      `FIL-${ano}-${Math.random().toString(36).substring(2, 8).toUpperCase()}`;

    const verificarId = async (id: string): Promise<boolean> => {
      try {
        const data: any = await apiClient.get(`/affiliates/check-system-id?systemId=${id}`);
        return !!data.available;
      } catch {
        return false;
      }
    };

    try {
      // Tenta até 3 candidatos para garantir um ID disponível
      for (let tentativa = 0; tentativa < 3; tentativa++) {
        const candidato = gerarCandidato();
        const disponivel = await verificarId(candidato);
        if (disponivel) {
          setIdCustom(candidato);
          setIdDisponivel(true);
          return;
        }
      }
      // Se nenhum dos 3 passou, exibe aviso — não confirma sem checar
      setIdDisponivel(false);
      setErro('Não foi possível gerar um ID disponível. Tente novamente.');
    } catch {
      // Não seta idDisponivel(true) sem verificar — deixa null para o usuário tentar de novo
      setIdDisponivel(null);
      setErro('Erro ao verificar ID. Verifique a conexão e tente novamente.');
    } finally {
      setGerandoId(false);
    }
  };

  const checarIdManual = async (id: string) => {
    if (!id.trim()) return;
    try {
      const data: any = await apiClient.get(`/affiliates/check-system-id?systemId=${id}`);
      setIdDisponivel(data.available);
    } catch { setIdDisponivel(null); }
  };

  // ─── FEPCSSP ──────────────────────────────────────────────────────────────
  const gerarFepcssp = async () => {
    setGerandoFepcssp(true); setFepcsspDisponivel(null);
    try {
      const data: any = await apiClient.get('/professionals/next-fepcssp');
      const numero = data.registroFepcssp ?? '';
      setFepcsspCustom(numero);
      setFepcsspDisponivel(true);
    } catch {
      setFepcsspCustom('');
      setFepcsspDisponivel(null);
    } finally { setGerandoFepcssp(false); }
  };

  const checarFepcssp = async (numero: string) => {
    if (!numero.trim()) return;
    try {
      const data: any = await apiClient.get(
        `/affiliates/check-fepcssp?numero=${encodeURIComponent(numero.trim())}`,
      );
      setFepcsspDisponivel(data.available);
    } catch { setFepcsspDisponivel(null); }
  };

  // ─── Validações ───────────────────────────────────────────────────────────
  const validar1 = () => {
    const e: Record<string, string> = {};
    if (!etapa1.nomeSolicitante.trim()) e.nomeSolicitante = 'Obrigatório';
    if (etapa1.cpf.replace(/\D/g,'').length !== 11) e.cpf = 'CPF inválido';
    if (!etapa1.email.includes('@')) e.email = 'E-mail inválido';
    if (!etapa1.endereco.trim()) e.endereco = 'Obrigatório';
    if (etapa1.cep.replace(/\D/g,'').length !== 8) e.cep = 'CEP inválido';
    if (fepcsspCustom.trim() && fepcsspDisponivel === false) e.fepcssp = 'Número já cadastrado';
    if (etapa1.identidadeInternacional && !/^[A-Z0-9]{15}$/.test(etapa1.identidadeInternacional)) {
      e.identidadeInternacional = 'Informe exatamente 15 letras ou números';
    }
    // ✅ Bloqueia avanço se ID foi digitado mas não verificado ou está em uso
    if (idCustom.trim() && idDisponivel === false) e.systemId = 'ID já está em uso — gere outro';
    setErros(e); return Object.keys(e).length === 0;
  };
  const validar2 = () => {
    const e: Record<string, string> = {};
    if (etapa2.experienciaAnos < 1) e.experienciaAnos = 'Mínimo 1 ano';
    if (!etapa2.especializacoes.trim()) e.especializacoes = 'Obrigatório';
    setErros(e); return Object.keys(e).length === 0;
  };

  // ─── Submit cadastro ──────────────────────────────────────────────────────
  const handleSubmitCadastro = async () => {
    if (!validar1() || !validar2()) return;
    setSalvando(true); setErro(null);
    try {
      const payload = {
        nomeSolicitante:  etapa1.nomeSolicitante                                        || undefined,
        schoolName:       etapa1.nomeFantasia?.trim() || etapa1.nomeSolicitante?.trim() || undefined,
        cpf:              etapa1.cpf                                                    || undefined,
        cnpj:             etapa1.cnpj                                                   || undefined,
        email:            etapa1.email                                                  || undefined,
        address:          etapa1.endereco                                               || undefined,
        endereco:         etapa1.endereco                                               || undefined,
        cep:              etapa1.cep                                                    || undefined,
        city:             etapa1.cidade                                                 || undefined,
        state:            etapa1.estado                                                 || undefined,
        phone:            etapa1.telefone                                               || undefined,
        dataNascimento:   etapa1.dataNascimento                                         || undefined,
        especializacoes:  etapa2.especializacoes                                        || undefined,
        experienciaAnos:  etapa2.experienciaAnos                                        || undefined,
        funcao:           etapa2.funcao                                                 || undefined,
        isSchool:         etapa1.isSchool,
        website:          etapa1.website                                                || undefined,
        identidadeInternacional: etapa1.identidadeInternacional                         || undefined,
        // ✅ Escola vinculada
        ...(escolaVinculada ? { schoolId: escolaVinculada.id } : {}),
        // ✅ ID e FEPCSSP — só envia se verificado como disponível
        ...(idCustom.trim() && idDisponivel === true ? { systemId: idCustom.trim() } : {}),
        ...(fepcsspCustom.trim() ? { registroFepcssp: fepcsspCustom.trim() } : {}),
      };

      const data: any = await apiClient.post('/affiliates/admin/create', payload);
      const affiliateData = data.affiliate ?? data;

      setAffiliateId(affiliateData.id);
      setAffiliateUserId(affiliateData.userId ?? null);
      setSystemId(data.systemId ?? affiliateData.systemId ?? null);
      setRegistroFepcssp(
        data.registroFepcssp ?? affiliateData.registroFepcssp ?? null,
      );

      setEtapa(3);
    } catch (e: any) {
      setErro(e.message ?? 'Erro ao cadastrar filiado');
    } finally { setSalvando(false); }
  };

  // ─── Upload individual ────────────────────────────────────────────────────
  const uploadSlot = async (
    slot: UploadSlot,
    setSlot: React.Dispatch<React.SetStateAction<UploadSlot>>,
    query: string,
    onSuccess?: (url: string) => Promise<void>,
  ): Promise<void> => {
    if (!slot.arquivo || !affiliateId || slot.enviado) return;
    setSlot(p => ({ ...p, enviando: true, erro: null }));
    try {
      const form = new FormData();
      form.append('file', slot.arquivo);
      const res: any = await apiClient.post(
        `/documents/public-upload?affiliateId=${encodeURIComponent(affiliateId)}&${query}`,
        form,
        { headers: { 'Content-Type': undefined } },
      );
      const url: string = res.url ?? res.fileUrl ?? res.path ?? '';
      if (!url) throw new Error('URL não retornada pelo servidor');
      if (onSuccess) await onSuccess(url);
      setSlot(p => ({ ...p, url, enviado: true, enviando: false }));
    } catch (e: any) {
      setSlot(p => ({ ...p, enviando: false, erro: e.message ?? 'Erro no upload' }));
    }
  };

  // ─── Finalizar ────────────────────────────────────────────────────────────
  const handleFinalizar = async () => {
    if (!affiliateId) return;
    setFinalizando(true);
    await uploadSlot(slotLogo, setSlotLogo,
      'documentType=foto&description=Logo%20da%20escola',
      async url => { await apiClient.patch(`/affiliates/${affiliateId}`, { logoUrl: url }); },
    );
    await uploadSlot(slotFoto, setSlotFoto,
      'documentType=foto&description=Foto%20de%20perfil',
      async url => {
        if (affiliateUserId) {
          await apiClient.patch(`/users/${affiliateUserId}`, { photoUrl: url }).catch(() => {});
        }
      },
    );
    await uploadSlot(slotDocumento, setSlotDocumento,
      'documentType=cpf&description=Documento%20de%20identidade',
    );
    setFinalizando(false);
    setSucesso(true);
    setTimeout(() => { onSucesso(); }, 1800);
  };

  const algumArquivo     = !!(slotLogo.arquivo || slotFoto.arquivo || slotDocumento.arquivo);
  const qualquerEnviando = slotLogo.enviando || slotFoto.enviando || slotDocumento.enviando || finalizando;

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <div>
            <h2 className="text-xl font-black text-gray-900">Novo Cadastro de Filiado</h2>
            <p className="text-sm text-gray-500">Etapa {etapa} de 3</p>
          </div>
          <div className="flex items-center gap-4">
            {[1,2,3].map(n => (
              <div key={n} className="flex items-center gap-1.5">
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-black border-2 transition-colors
                  ${etapa > n ? 'bg-green-500 border-green-500 text-white'
                  : etapa === n ? 'bg-blue-600 border-blue-600 text-white'
                  : 'bg-white border-gray-300 text-gray-400'}`}>
                  {etapa > n ? '✓' : n}
                </div>
                <span className={`text-xs font-semibold hidden md:block ${etapa >= n ? 'text-blue-600' : 'text-gray-400'}`}>
                  {n === 1 ? 'Identificação' : n === 2 ? 'Complementar' : 'Fotos e Docs'}
                </span>
                {n < 3 && <div className="w-6 h-px bg-gray-300 hidden md:block" />}
              </div>
            ))}
            <button onClick={onFechar} className="p-2 hover:bg-gray-200 rounded-lg text-gray-500 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {erro && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-semibold">{erro}</p>
            </div>
          )}
          {sucesso && (
            <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700">
              <CheckCircle className="w-5 h-5 flex-shrink-0" />
              <p className="text-sm font-bold">Filiado cadastrado com sucesso!</p>
            </div>
          )}

          {/* ═══ ETAPA 1 ════════════════════════════════════════════════════ */}
          {etapa === 1 && (
            <div className="space-y-4">

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Nome completo *</label>
                <input type="text" value={etapa1.nomeSolicitante}
                  onChange={e => setEtapa1(p => ({ ...p, nomeSolicitante: e.target.value }))}
                  className={`${inputBase} ${erros.nomeSolicitante ? 'border-red-500' : ''}`}
                  placeholder="Nome do responsável ou escola" />
                {erros.nomeSolicitante && <p className="text-red-500 text-xs mt-1">{erros.nomeSolicitante}</p>}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">CPF *</label>
                  <input type="text" value={etapa1.cpf}
                    onChange={e => setEtapa1(p => ({ ...p, cpf: e.target.value.replace(/\D/g,'').slice(0,11) }))}
                    className={`${inputBase} ${erros.cpf ? 'border-red-500' : ''}`} placeholder="Somente números" />
                  {erros.cpf && <p className="text-red-500 text-xs mt-1">{erros.cpf}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">CNPJ (opcional)</label>
                  <input type="text" value={etapa1.cnpj}
                    onChange={e => setEtapa1(p => ({ ...p, cnpj: e.target.value.replace(/\D/g,'').slice(0,14) }))}
                    className={inputBase} placeholder="14 dígitos" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Nome fantasia</label>
                  <input type="text" value={etapa1.nomeFantasia}
                    onChange={e => setEtapa1(p => ({ ...p, nomeFantasia: e.target.value }))}
                    className={inputBase} placeholder="Studio, Escola..." />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Data de nascimento</label>
                  <input type="date" value={etapa1.dataNascimento}
                    onChange={e => setEtapa1(p => ({ ...p, dataNascimento: e.target.value }))}
                    className={inputBase} />
                </div>
              </div>

              {/* ── ID do Filiado ─────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
                  ID do Filiado
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input type="text" value={idCustom}
                      onChange={e => { setIdCustom(e.target.value.toUpperCase()); setIdDisponivel(null); }}
                      onBlur={e => checarIdManual(e.target.value)}
                      className={`${inputBase} pr-8 font-mono
                        ${erros.systemId || idDisponivel === false ? 'border-red-500'
                        : idDisponivel === true ? 'border-green-500' : ''}`}
                      placeholder="FIL-2026-XXXXXX (deixe em branco para gerar)" />
                    {idDisponivel === true  && <CheckCircle className="absolute right-3 top-3.5 w-4 h-4 text-green-500" />}
                    {idDisponivel === false && <AlertCircle className="absolute right-3 top-3.5 w-4 h-4 text-red-500" />}
                  </div>
                  <button type="button" onClick={gerarIdAutomatico} disabled={gerandoId}
                    className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700
                      flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
                    {gerandoId ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Gerar ID
                  </button>
                </div>
                {erros.systemId    && <p className="text-red-500 text-xs mt-1">{erros.systemId}</p>}
                {!erros.systemId && idDisponivel === false && <p className="text-red-500 text-xs mt-1">ID já existe — gere um novo</p>}
                {!erros.systemId && idDisponivel === true  && <p className="text-green-600 text-xs mt-1">✓ ID disponível e verificado</p>}
              </div>

              {/* ── Nº FEPCSSP ───────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Nº FEPCSSP
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type="text"
                      value={fepcsspCustom}
                      onChange={e => {
                        setFepcsspCustom(e.target.value.replace(/\D/g, '').slice(0, 6));
                        setFepcsspDisponivel(null);
                      }}
                      onBlur={e => checarFepcssp(e.target.value)}
                      className={`${inputBase} pr-8 font-mono
                        ${fepcsspDisponivel === false ? 'border-red-500'
                        : fepcsspDisponivel === true  ? 'border-green-500' : ''}`}
                      placeholder="000001 (deixe em branco para gerar automático)"
                      maxLength={6}
                    />
                    {fepcsspDisponivel === true  && <CheckCircle className="absolute right-3 top-3.5 w-4 h-4 text-green-500" />}
                    {fepcsspDisponivel === false && <AlertCircle className="absolute right-3 top-3.5 w-4 h-4 text-red-500" />}
                  </div>
                  <button type="button" onClick={gerarFepcssp} disabled={gerandoFepcssp}
                    className="px-4 py-3 bg-blue-600 text-white rounded-xl font-bold text-sm hover:bg-blue-700
                      flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
                    {gerandoFepcssp ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Gerar
                  </button>
                </div>
                {fepcsspDisponivel === false && <p className="text-red-500 text-xs mt-1">Número já cadastrado — informe outro ou gere um novo</p>}
                {fepcsspDisponivel === true  && <p className="text-green-600 text-xs mt-1">✓ Número disponível</p>}
                {erros.fepcssp && <p className="text-red-500 text-xs mt-1">{erros.fepcssp}</p>}
              </div>

              {/* ── Identidade internacional ─────────────────────────────── */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Identidade internacional <span className="text-gray-400 normal-case font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={etapa1.identidadeInternacional}
                  onChange={e => setEtapa1(p => ({
                    ...p,
                    identidadeInternacional: e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 15),
                  }))}
                  className={`${inputBase} font-mono ${erros.identidadeInternacional ? 'border-red-500' : ''}`}
                  placeholder="AB12CD34EF56GH7"
                  maxLength={15}
                />
                <div className="flex justify-between mt-1">
                  <p className="text-xs text-gray-400">Exatamente 15 caracteres, somente letras e números</p>
                  <p className="text-xs text-gray-400 font-mono">{etapa1.identidadeInternacional.length}/15</p>
                </div>
                {erros.identidadeInternacional && <p className="text-red-500 text-xs mt-1">{erros.identidadeInternacional}</p>}
              </div>

              {/* ── Vincular à Escola ─────────────────────────────────────── */}
              <BuscaEscola
                escolaSelecionada={escolaVinculada}
                onSelecionar={setEscolaVinculada}
              />

              {/* ── CEP ──────────────────────────────────────────────────── */}
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">CEP *</label>
                <div className="flex gap-2">
                  <input type="text" value={etapa1.cep}
                    onChange={e => setEtapa1(p => ({ ...p, cep: e.target.value.replace(/\D/g,'').slice(0,8) }))}
                    className={`${inputBase} flex-1 ${erros.cep ? 'border-red-500' : ''}`} placeholder="00000000" />
                  <button type="button" onClick={() => buscarCEP(etapa1.cep)}
                    className="px-4 py-3 bg-gray-100 border border-gray-300 text-gray-700 rounded-xl font-bold text-sm hover:bg-gray-200 flex items-center gap-2">
                    {cepLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                    Buscar
                  </button>
                </div>
                {erros.cep && <p className="text-red-500 text-xs mt-1">{erros.cep}</p>}
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Endereço *</label>
                <input type="text" value={etapa1.endereco}
                  onChange={e => setEtapa1(p => ({ ...p, endereco: e.target.value }))}
                  className={`${inputBase} ${erros.endereco ? 'border-red-500' : ''}`} placeholder="Rua, Av..." />
                {erros.endereco && <p className="text-red-500 text-xs mt-1">{erros.endereco}</p>}
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Cidade</label>
                  <input type="text" value={etapa1.cidade}
                    onChange={e => setEtapa1(p => ({ ...p, cidade: e.target.value }))} className={inputBase} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">UF</label>
                  <input type="text" value={etapa1.estado}
                    onChange={e => setEtapa1(p => ({ ...p, estado: e.target.value.slice(0,2).toUpperCase() }))}
                    className={inputBase} maxLength={2} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Complemento</label>
                  <input type="text" value={etapa1.complemento}
                    onChange={e => setEtapa1(p => ({ ...p, complemento: e.target.value }))} className={inputBase} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">E-mail *</label>
                  <input type="email" value={etapa1.email}
                    onChange={e => setEtapa1(p => ({ ...p, email: e.target.value }))}
                    className={`${inputBase} ${erros.email ? 'border-red-500' : ''}`} placeholder="email@dominio.com" />
                  {erros.email && <p className="text-red-500 text-xs mt-1">{erros.email}</p>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Telefone</label>
                  <input type="text" value={etapa1.telefone}
                    onChange={e => setEtapa1(p => ({ ...p, telefone: e.target.value }))}
                    className={inputBase} placeholder="(00) 00000-0000" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Site da Escola (opcional)</label>
                <input type="url" value={etapa1.website}
                  onChange={e => setEtapa1(p => ({ ...p, website: e.target.value }))}
                  className={inputBase} placeholder="https://www.minhaescola.com.br" />
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl border-2 border-dashed border-amber-300 bg-amber-50">
                <div>
                  <p className="text-sm font-bold text-gray-800">Exibir no Portal de Escolas?</p>
                  <p className="text-xs text-gray-500 mt-0.5">Marque se este filiado é uma escola credenciada</p>
                </div>
                <button type="button"
                  onClick={() => setEtapa1(p => ({ ...p, isSchool: !p.isSchool }))}
                  className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ${etapa1.isSchool ? 'bg-amber-500' : 'bg-gray-300'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${etapa1.isSchool ? 'translate-x-6' : 'translate-x-0'}`} />
                </button>
              </div>
            </div>
          )}

          {/* ═══ ETAPA 2 ════════════════════════════════════════════════════ */}
          {etapa === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Função / Atuação</label>
                  <input type="text" value={etapa2.funcao}
                    onChange={e => setEtapa2(p => ({ ...p, funcao: e.target.value }))}
                    className={inputBase} placeholder="Cabeleireiro, Instrutor..." />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Início na área</label>
                  <input type="date" value={etapa2.dataInicioAtividade}
onChange={e => setEtapa2(p => ({ ...p, dataInicioAtividade: e.target.value }))}                    className={inputBase} />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Anos de experiência *</label>
                <input type="number" value={etapa2.experienciaAnos || ''}
                  onChange={e => setEtapa2(p => ({ ...p, experienciaAnos: parseInt(e.target.value) || 0 }))}
                  className={`${inputBase} ${erros.experienciaAnos ? 'border-red-500' : ''}`} min={0} />
                {erros.experienciaAnos && <p className="text-red-500 text-xs mt-1">{erros.experienciaAnos}</p>}
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1.5 uppercase tracking-wide">Especializações *</label>
                <textarea value={etapa2.especializacoes}
                  onChange={e => setEtapa2(p => ({ ...p, especializacoes: e.target.value }))}
                  rows={4} className={`${inputBase} resize-none ${erros.especializacoes ? 'border-red-500' : ''}`}
                  placeholder="Corte, coloração, tratamentos..." />
                {erros.especializacoes && <p className="text-red-500 text-xs mt-1">{erros.especializacoes}</p>}
              </div>
            </div>
          )}

          {/* ═══ ETAPA 3 — Fotos, Docs e confirmação ════════════════════════ */}
          {etapa === 3 && (
            <div className="space-y-5">
              {(systemId || registroFepcssp) && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <p className="text-sm text-green-700 font-semibold">Filiado cadastrado e aprovado</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {systemId && (
                      <div className="bg-white rounded-lg p-3 border border-green-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">ID do Filiado</p>
                        <p className="text-base font-black text-green-800 font-mono">{systemId}</p>
                      </div>
                    )}
                    {registroFepcssp && (
                      <div className="bg-white rounded-lg p-3 border border-green-100">
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Nº FEPCSSP</p>
                        <p className="text-base font-black text-green-800 font-mono">{registroFepcssp}</p>
                      </div>
                    )}
                  </div>
                  {escolaVinculada && (
                    <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-lg border border-blue-100">
                      <Link2 className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                      <p className="text-xs text-blue-700 font-semibold">
                        Vinculado à escola: <span className="font-mono">{escolaVinculada.schoolName}</span>
                      </p>
                    </div>
                  )}
                  <p className="text-xs text-green-600 mt-3">
                    Senha padrão: <span className="font-mono font-bold">fil@feder26</span>
                  </p>
                </div>
              )}

              {!sucesso ? (
                <>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    Todos os campos são opcionais — mas a{' '}
                    <span className="font-semibold text-gray-700">logomarca é recomendada</span>,
                    pois será impressa nos diplomas dos alunos desta escola.
                  </p>
                  <div className="space-y-3">
                    <UploadCard
                      icon={<Building2 className="w-4 h-4" />}
                      titulo="Logomarca da Escola"
                      descricao="Impressa nos diplomas e certificados dos alunos"
                      badge="Diploma"
                      slot={slotLogo} accept="image/*"
                      onSelecionar={f => selecionarArquivo(setSlotLogo, f)}
                      onRemover={() => removerArquivo(setSlotLogo, slotLogo)}
                    />
                    <UploadCard
                      icon={<User className="w-4 h-4" />}
                      titulo="Foto de Perfil do Responsável"
                      descricao="Exibida no painel do filiado"
                      slot={slotFoto} accept="image/*"
                      onSelecionar={f => selecionarArquivo(setSlotFoto, f)}
                      onRemover={() => removerArquivo(setSlotFoto, slotFoto)}
                    />
                    <UploadCard
                      icon={<FileText className="w-4 h-4" />}
                      titulo="Documento de Identidade"
                      descricao="CPF, RG ou CNH — arquivado na plataforma (opcional)"
                      slot={slotDocumento} accept="image/*,.pdf"
                      onSelecionar={f => selecionarArquivo(setSlotDocumento, f)}
                      onRemover={() => removerArquivo(setSlotDocumento, slotDocumento)}
                    />
                  </div>
                  {(slotLogo.erro || slotFoto.erro || slotDocumento.erro) && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl space-y-0.5">
                      <p className="text-xs font-semibold text-red-700">Erros no envio:</p>
                      {slotLogo.erro      && <p className="text-xs text-red-600">Logo: {slotLogo.erro}</p>}
                      {slotFoto.erro      && <p className="text-xs text-red-600">Foto: {slotFoto.erro}</p>}
                      {slotDocumento.erro && <p className="text-xs text-red-600">Documento: {slotDocumento.erro}</p>}
                    </div>
                  )}
                </>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-3 text-center">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                  <p className="text-lg font-black text-green-700">Tudo salvo com sucesso!</p>
                  <p className="text-sm text-gray-500">O filiado está pronto para usar o sistema.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between flex-shrink-0">
          <button
            onClick={() => etapa > 1 ? setEtapa((etapa - 1) as 1|2|3) : onFechar()}
            disabled={etapa === 3}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl border-2 border-gray-300 text-gray-700
              font-bold hover:bg-gray-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
            <ChevronLeft className="w-4 h-4" />
            {etapa === 1 ? 'Cancelar' : 'Anterior'}
          </button>
          {etapa === 1 && (
            <button onClick={() => validar1() && setEtapa(2)}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-colors">
              Próxima <ChevronRight className="w-4 h-4" />
            </button>
          )}
          {etapa === 2 && (
            <button onClick={handleSubmitCadastro} disabled={salvando}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 disabled:opacity-60 transition-colors">
              {salvando
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Salvando...</>
                : <>Cadastrar <ChevronRight className="w-4 h-4" /></>}
            </button>
          )}
          {etapa === 3 && !sucesso && (
            <div className="flex items-center gap-3">
              <button onClick={onSucesso} disabled={qualquerEnviando}
                className="px-4 py-2.5 rounded-xl border-2 border-gray-300 text-gray-600 font-bold text-sm
                  hover:bg-gray-100 disabled:opacity-40 transition-colors">
                Pular tudo
              </button>
              <button onClick={handleFinalizar} disabled={qualquerEnviando || !algumArquivo}
                className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-green-600 text-white font-bold
                  hover:bg-green-700 disabled:opacity-50 transition-colors">
                {qualquerEnviando
                  ? <><Loader2 className="w-4 h-4 animate-spin" /> Enviando...</>
                  : <><Upload className="w-4 h-4" /> Enviar e Finalizar</>}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModalCadastroFiliado;
